import { FastField, Field, FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/system';
import { formatCurrency, formatPercentage } from '@shared/commonFunctions';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { useAppDispatch } from '@store/hooks';
import { IOption } from '@shared/interfaces';
import { FooterSection, LenderSectionRow, LenderSectionRowValidation } from '@styles/ContactManager/DealCalculator/LenderSectionStyles';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { footerLenderSelector, setFooterLenderAction } from '../../../../../../../../../../store/reducers/dealCalculatorSlice';
import { DealCalculatorModel } from '../../../../models/DealCalculatorModel';
import { IEligibleLender } from '../../../../models/EligibleLenderModel';
import { ILenderFooterState } from '../../../../interfaces';
import { ILenderResponseFooter } from './LenderSectionProducts';
import { addDays, dailyCalculations, getDti, getPti, PMT } from './lernderCalculationUtils';

interface ILenderResultProps {
  lender: IEligibleLender;
  idx: number;
  formProps: FormikProps<DealCalculatorModel>;
  getColorCoding: (value: number, valueYellow: number, valueGreen: number) => string;
  netRate: number;
  subTotalBeforeTax: () => number;
  salesTax: number;
  productSalesTax: number;
  isTotalAmountFinancedRed: () => string;
  totalAmountFinanced: number;
  checkLenderCode: (code: string) => boolean;
  lenderResultFooterData?: ILenderResponseFooter;
  rateSaving: number;
  lienPayment: number;
  lenderRateOptions: IOption[];
  buyRate: number;
  rateAdjustment: number;
  isExpanded: (section: string) => boolean;
  isSelected: boolean;
}

interface IRateBlock {
  isExpanded(section: string): boolean;
  buyRate: number;
  netRate: number;
  totalRateAdjustment: number;
}

const RateBlock = ({
  buyRate = 0,
  netRate = 0,
  totalRateAdjustment = 0,
  isExpanded
}: IRateBlock): JSX.Element | null => {
  return isExpanded('rateBlock') ? <Box height={120}>
      <LenderSectionRow>
        <b>{formatPercentage(buyRate)}</b>
      </LenderSectionRow>
      <LenderSectionRow>
        <b>{formatCurrency(totalRateAdjustment)}</b>
      </LenderSectionRow>
      <LenderSectionRow>
        <b>{formatCurrency(netRate)}</b>
      </LenderSectionRow>
    </Box> : null
  ;
};

const LenderResult = ({
  lender,
  idx,
  formProps,
  getColorCoding,
  netRate,
  subTotalBeforeTax,
  salesTax,
  productSalesTax,
  isTotalAmountFinancedRed,
  totalAmountFinanced,
  checkLenderCode,
  lenderResultFooterData,
  rateSaving,
  lienPayment,
  lenderRateOptions,
  buyRate,
  rateAdjustment,
  isExpanded,
  isSelected,
}: ILenderResultProps) => {
  const dispatch = useAppDispatch();
  const { values } = formProps;
  const monthlyIncome = values?.globalInputs?.monthlyIncome || 0;
  const localTerm = lender.inputs?.term || 0;
  const [firstPayment, setFirstPayment] = useState(0);
  const [lastPayment, setLastPayment] = useState(0);

 const footerLender: Partial<ILenderFooterState> | null = useSelector(footerLenderSelector);

  useEffect(() => {
    const contractDate = lender?.inputs?.contractDate || new Date().toString();
    const firstPaymentDays = lender?.inputs?.firstPaymentDays || 0;
    const firstPaymentDate = addDays(contractDate, firstPaymentDays).toString();

    if (checkLenderCode('mat')) {
      const calculationResult = dailyCalculations(
        totalAmountFinanced,
        netRate / 100,
        localTerm,
        contractDate,
        firstPaymentDate,
      );

      setFirstPayment(calculationResult?.firstPaymentAmount || 0);
      setLastPayment(calculationResult?.lastPaymentAmount || 0);
    } else {
      const payment = Number(
        (-1 * PMT(netRate / 1200, localTerm, totalAmountFinanced, firstPaymentDays, 0, 0)).toFixed(),
      );

      setFirstPayment(payment);
      setLastPayment(payment);
    }
  });

  const totalPti = () => {
    if (monthlyIncome === 0) {
      return 0;
    }
    return (firstPayment / monthlyIncome) * 100;
  };

  useEffect(() => {
    isSelected && 
      dispatch(
        setFooterLenderAction({
          payment: firstPayment,
        })
      );
  }, [firstPayment, isSelected]);

  return (
    <>
      <LenderSectionRow>
        <FastField
          hideNoneOption
          removeExtraPadding
          backgroundColor={'red'}
          name={`eligibleLenders[${idx}].inputs.rateMarkup`}
          component={SelectAutocompleteField}
          options={lenderRateOptions}
        />
      </LenderSectionRow>
      <LenderSectionRow>
        <Field
          suffix="%"
          name={`eligibleLenders[${idx}].inputs.approvedRate`}
          component={NumberInputField}
          decimalScale={2}
          sx={{
            '& input': {
              backgroundColor: netRate !== (lender.inputs?.approvedRate || netRate) ? '#ffc107' : 'transparent',
            },
          }}
        />
      </LenderSectionRow>
      <RateBlock
        buyRate={buyRate}
        netRate={netRate}
        totalRateAdjustment={rateAdjustment}
        isExpanded={isExpanded}
      />
      {/* Lender Results  Block */}
      <LenderSectionRow>
        <b>{formatCurrency(subTotalBeforeTax())}</b>
      </LenderSectionRow>
      <LenderSectionRow>
        <b>{formatCurrency(salesTax)}</b>
      </LenderSectionRow>
      <LenderSectionRow>
        <b>{formatCurrency(productSalesTax)}</b>
      </LenderSectionRow>
      <LenderSectionRowValidation
        style={{
          backgroundColor: isTotalAmountFinancedRed(),
        }}
      >
        <b>{formatCurrency(totalAmountFinanced)}</b>
      </LenderSectionRowValidation>
      <LenderSectionRow>
        <b>{formatCurrency(firstPayment)}</b>
      </LenderSectionRow>
      <LenderSectionRow>
        <b>{formatCurrency(lastPayment)}</b>
      </LenderSectionRow>
      <LenderSectionRowValidation
        style={{
          backgroundColor: getColorCoding(
            lender?.actualDti ?? 0,
            getDti(lender, monthlyIncome).maxDtiGreen || 0,
            getDti(lender, monthlyIncome).maxDtiYellow || 0
          ),
        }}
      >
        <b>
          {formatPercentage(getDti(lender, monthlyIncome).maxDtiGreen)} / {(footerLender?.newDTI  ?? 0) === 0 ? 'Needs income' : formatPercentage(footerLender?.newDTI)}
        </b>
      </LenderSectionRowValidation>
      <LenderSectionRowValidation
        style={{
          backgroundColor: getColorCoding(totalPti(), getPti(lender).maxPtiGreen || 0, getPti(lender).maxPtiYellow || 0),
        }}
      >
        <b>
          {formatPercentage(getPti(lender).maxPtiGreen)} / {totalPti() === 0 ? 'Needs income' : formatPercentage(totalPti())}
        </b>
      </LenderSectionRowValidation>
      <LenderSectionRow>
        <b>{idx + 1}</b>
      </LenderSectionRow>
      <FooterSection style={{ paddingBottom: '5px' }}>
        <LenderSectionRow>
          <b>{formatCurrency((lenderResultFooterData && lenderResultFooterData?.totalGp) || 0)}</b>
        </LenderSectionRow>
        <LenderSectionRow>
          <b>{formatCurrency((lenderResultFooterData && lenderResultFooterData?.gpVrp) || 0)}</b>
        </LenderSectionRow>
        <LenderSectionRow>
          <b>{`${Number(rateSaving ?? 0)?.toFixed(2)}%`}</b>
        </LenderSectionRow>
        <LenderSectionRow>
          <b>{formatCurrency(lienPayment - firstPayment)}</b>
        </LenderSectionRow>
        <LenderSectionRow>
          <b>{formatCurrency(Math.max(lender.autoSubmitSelectedTierCondition?.requiredDownPaymentGreen || 0, 0))}</b>
        </LenderSectionRow>
      </FooterSection>
    </>
  );
};

export default LenderResult;
